/**
 * @file schema.ts
 * @description Документ оформления сайта v2 (phase19.16 Р1; SB120 этап 2 —
 * «оформление сайта числами»): zod-схема, нейтральное умолчание платформы,
 * слияние с умолчаниями, подъём версий и чтение сохранённого документа
 * (db-design-r1 §1.1, db-design-sb120-stage2 §1–§2; SB4, SB8–SB11, SB13,
 * SB14, SB73, SB89, SB90, SB120).
 *
 * Документ хранится в `SiteDraft.theme` (черновик), `SiteRevision.theme`
 * (опубликованное) и `SiteDesign.theme` (дизайн), версия — `SITE_THEME_SCHEMA`.
 * Документ v1 (ступени) поднимается `liftThemeV1` (legacy-v1.ts) **при любой
 * версии** — первой строкой `fillSiteThemeDefaults`: так кэши витрины со
 * старым JSON, вызовы с версией по умолчанию и старая вкладка кабинета
 * (мост записи) не теряют формы и рамку. Пределы чисел — theme-limits.ts.
 * Части документа:
 *   colors   — 9 ролей SB8; производные (текст на кнопке, наведение, линии,
 *              фокус, служебные) НЕ хранятся — их считает derive.ts (SB9);
 *   fonts    — ключи каталога fonts.ts или свои шрифты `shop:<id>` (SB99,
 *              §7.12) по трём ролям SB10;
 *   type     — начертание, регистр и размер заголовков (множитель),
 *              надзаголовки и их разрядка в em (SB73, SB120);
 *   shapes   — скругление кнопок px или «Пилюля», регистр кнопок, скругление
 *              карточек и полей px, рамка и тень карточек (SB11, SB120);
 *   backdrop — задник сайта: выключен или до 3 рисунков из медиатеки по x/y
 *              с шириной px; свечение силой % и цветом (SB11, SB120);
 *   frame    — «рамка сайта»: ширина 960–1920 px, тень % (SB89);
 *   header   — место логотипа, тон, заливка (роль или свой цвет, SB8
 *              2026-10-11, surface-fill.ts), состав бренда, регистр меню,
 *              «логотип крупнее шапки» (SB13, SB73, SB90);
 *   footer   — шаблон, тон, заливка, что показывать (SB14, uiux-design-r1 §2),
 *              вид нижней полосы `footer.legal` (SB118, footer-legal.ts);
 *   spacing  — плотность отступов сайта (решение пользователя 2026-10-03,
 *              фаза §7.13; с SB120 этапа 2 — множитель 0,5–1,6); доля отступов
 *              секций на телефоне `phoneFactor` (SB120 этап 1, 0–1, 0,6);
 *   cart     — вид корзины: панель или окно, размеры, подложка, цвета,
 *              фото, что после «В корзину» (SB120 этап 4, cart.ts;
 *              db-design-sb120-stage4). Версия схемы та же — раздела нет,
 *              читается умолчание «как сейчас»;
 *   footerDocs — свои строки блока «Ссылки» подвала: документ, группа или
 *              «Все документы» с якорем после ссылки платформы (SB142,
 *              footer-docs.ts, db-design-legal-docs §6.1). Версия та же —
 *              раздела нет, читается «своих строк нет»; битая строка
 *              снимается по одной (`SITE_THEME_SECTION_REPAIRS`);
 *   consentBanner — вид баннера согласия на cookie: полоса или карточка в
 *              углу, ширина карточки, угол, заливка и цвет текста (Р3г,
 *              SB134, consent-banner.ts, db-design-r3g §2.3). Версия та же —
 *              раздела нет, читается нынешняя полоса.
 *
 * Части перечислены исчерпывающим списком `SITE_THEME_SECTIONS`: незнакомая
 * часть (раздел будущей версии после отката) при чтении молча отбрасывается
 * — на этом держится безопасный откат добавочных этапов.
 *
 * Схема строгая (лишний ключ — ошибка): она проверяет то, что пишет кабинет.
 * Сохранённый документ читается через `readSiteTheme` — он не падает, а
 * подставляет умолчания и сообщает, что заменил (fail fast — для записи,
 * деградация — для чтения).
 *
 * Чайберийских значений здесь нет (§7.0, SB1): нынешний вид Chaiberi —
 * данные, их пишет команда импорта `site:import-view`.
 */

import { z } from "zod";

import {
  isShopFontKey,
  isSiteFontKey,
  PLATFORM_FONT_REGISTRY,
  SITE_SYSTEM_FONT_KEY,
  type SiteFontRegistry,
} from "./fonts.js";
import { SITE_COLOR_PATTERN, SITE_THEME_SCHEMA, type SiteColorRole } from "./keys.js";
import { defaultSiteFooterLegal, siteFooterLegalSchema } from "./footer-legal.js";
import { SITE_LIMITS } from "./limits.js";
import { SECTION_LOOK_LIMITS } from "../site-section/look-limits.js";
import { siteColorRefSchema } from "./color-ref.js";
import { HEADING_SCALE_V1, liftThemeV1 } from "./legacy-v1.js";
import { deriveMissingSurfaceFills, siteSurfaceFillSchema, syncSurfaceTones } from "./surface-fill.js";
import { SITE_THEME_LIMITS } from "./theme-limits.js";
import { DEFAULT_SITE_CART, siteCartSchema } from "./cart.js";
import { DEFAULT_SITE_CONSENT_BANNER, siteConsentBannerSchema } from "./consent-banner.js";
import { DEFAULT_SITE_FOOTER_DOCS, repairFooterDocs, siteFooterDocsSchema } from "./footer-docs.js";
import { deepFreeze } from "./deep-freeze.js";

/** Число в пределах (дробные разрешены): `num(L.headingScale)`. */
const num = (limits: { readonly min: number; readonly max: number }): z.ZodNumber =>
  z.number().finite().min(limits.min).max(limits.max);

const TL = SITE_THEME_LIMITS;

const hexColor = z.string().regex(SITE_COLOR_PATTERN, "Цвет — в формате #rrggbb или #rrggbbaa");

const colorRole = (): z.ZodString => hexColor;

export const siteColorsSchema = z.strictObject({
  pageBg: colorRole(),
  cardBg: colorRole(),
  panelBg: colorRole(),
  text: colorRole(),
  textMuted: colorRole(),
  button: colorRole(),
  accent: colorRole(),
  darkBg: colorRole(),
  onDark: colorRole(),
} satisfies Record<SiteColorRole, z.ZodString>);

/**
 * Ключ шрифта: каталог платформы или свой шрифт магазина `shop:<cuid>`
 * (SB99). Схема проверяет только синтаксис своего ключа — есть ли такой
 * шрифт у магазина, проверяет сервер по реестру (а `readSiteTheme` с
 * реестром магазина заменяет отсутствующий на системный).
 */
const fontKey = z
  .string()
  .refine(
    (key) => isSiteFontKey(key) || isShopFontKey(key),
    "Шрифта нет в каталоге платформы и среди ваших шрифтов",
  );

export const siteFontsSchema = z.strictObject({
  heading: fontKey,
  body: fontKey,
  /** Шрифт акцента (SB10, 2026-10-11); null — вместо него шрифт заголовков (uiux-design-r1 §1). */
  accent: fontKey.nullable(),
});

export const siteTypeSchema = z.strictObject({
  /** Начертание заголовков; CSS берёт ближайшее, что есть у шрифта (nearestFontWeight). */
  headingWeight: z.number().int().min(100).max(900).multipleOf(100),
  headingCase: z.enum(["asis", "upper"]),
  /**
   * Размер всех заголовков сайта без смены уровня H (SB10 «Размеры», SB120
   * этап 2): множитель `--site-heading-scale` 0,75–1,5 (css.ts). v1 s/m/l —
   * 0,875 / 1 / 1,125 (liftThemeV1); нет ключа — 1.
   */
  headingScale: num(TL.headingScale),
  /** Каким шрифтом надзаголовки («надпись капсом» над H1). */
  eyebrowFrom: z.enum(["body", "heading"]),
  /** Разрядка надписей капсом, em 0–0,4 (`--site-eyebrow-tracking`); v1 — 0 / 0,08 / 0,2. */
  eyebrowTracking: num(TL.eyebrowTracking),
});

/** Скругление карточек и фото (`card`) и полей и бейджей (`field`), px. */
export const siteRadiusSchema = z.strictObject({
  card: num(TL.radius),
  field: num(TL.radius),
});
export type SiteRadius = z.infer<typeof siteRadiusSchema>;

/**
 * Вид карточек: рамка (толщина целыми px, цвет — роль или свой; нет ключа —
 * линия сайта `--site-line`) и тень % (50 — прежняя «С тенью»). Тот же вид у
 * «Своей» карточки товара (catalog-options.ts).
 */
export const siteCardsSchema = z.strictObject({
  border: z.strictObject({
    width: z.number().int().min(TL.cardBorderWidth.min).max(TL.cardBorderWidth.max),
    color: siteColorRefSchema.optional(),
  }),
  shadow: num(TL.cardShadow),
});
export type SiteCards = z.infer<typeof siteCardsSchema>;

/**
 * Скругление кнопок: px 0–40 или «Пилюля» (`"pill"` — скругление по высоте
 * кнопки). Пилюля — литерал, а не 9999: `mergeMissingKeys` примитив не трогает,
 * а 9999 вышло бы за предел (тот же приём, что `look.container: number | "full"`).
 */
export const siteButtonRadiusSchema = z.union([num(TL.buttonRadius), z.literal("pill")]);
export type SiteButtonRadius = z.infer<typeof siteButtonRadiusSchema>;

export const siteShapesSchema = z.strictObject({
  /** v1 `buttonShape` square / rounded / pill → 0 / 10 / "pill" (ключ переименован). */
  buttonRadius: siteButtonRadiusSchema,
  /** Стрелка «→» в кнопках-ссылках. */
  buttonArrow: z.boolean(),
  buttonCase: z.enum(["asis", "upper"]),
  /** Скругление карточек, фото и полей; v1 none / soft / large → 0/0 · 8/6 · 20/12. */
  radius: siteRadiusSchema,
  /** v1 shadow / border / flat → тень 50 / рамка 1 px / ничего. */
  cards: siteCardsSchema,
});

/**
 * Рисунок фона (SB11, SB120 этап 2): место x/y в % области «окно без 96 px
 * сверху и 48 снизу» (рисунок сдвинут на себя на −x/−y %), ширина px на ПК
 * (от 1024), на планшете и телефоне — своё число или доля (нет ключа:
 * `SITE_BACKDROP_SIZE_SHARE`), яркость % (нет ключа — 30 / 35 с 768).
 * Геометрию на витрине считает `backdropImageBox` (css.ts).
 */
export const siteBackdropImageSchema = z.strictObject({
  /** id `ContentMediaAsset` магазина; принадлежность проверяет сервер. */
  mediaId: z.string().min(1).max(64),
  x: num(TL.backdropPosition),
  y: num(TL.backdropPosition),
  size: num(TL.backdropSize),
  sizeTablet: num(TL.backdropSize).optional(),
  sizePhone: num(TL.backdropSize).optional(),
  opacity: num(TL.backdropOpacity).optional(),
  hideOnMobile: z.boolean(),
});

/** Свечение (бывшая «дымка», SB89): сила % и цвет; нет цвета — акцент. */
export const siteHazeSchema = z.strictObject({
  strength: num(TL.hazeStrength),
  color: siteColorRefSchema.optional(),
});
export type SiteHaze = z.infer<typeof siteHazeSchema>;

/** Как лежит картинка «Фона страницы». */
export const SITE_PAGE_BG_MODES = ["cover", "tile", "fixed"] as const;

/**
 * «Фон страницы» — картинка под всем сайтом (решение пользователя
 * 2026-10-06: «задник» стал универсальным фоном). `cover` — растянуть на
 * экран, `tile` — плиткой, `fixed` — растянуть и не прокручивать.
 * `opacity` 0,1–1 (умолчание 1), `dim` — затемнение 0–0,6 (умолчание 0).
 */
export const sitePageBgImageSchema = z.strictObject({
  /** id `ContentMediaAsset` магазина; принадлежность и тип проверяет сервер. */
  mediaId: z.string().min(1).max(64),
  mode: z.enum(SITE_PAGE_BG_MODES),
  opacity: z.number().min(0.1).max(1),
  dim: z.number().min(0).max(0.6),
  hideOnMobile: z.boolean(),
});

export const siteBackdropSchema = z.strictObject({
  mode: z.enum(["off", "images"]),
  /** «Свечение» — мягкие пятна цвета по углам (SB89); v1 true / false → 18 / 0 %. */
  haze: siteHazeSchema,
  images: z
    .array(siteBackdropImageSchema)
    .max(SITE_LIMITS.backdropImages, `Рисунков на фоне страницы — не больше ${SITE_LIMITS.backdropImages}`),
  /** «Фон страницы»: картинка под всем сайтом; null — нет. Не зависит от `mode`. */
  image: sitePageBgImageSchema.nullable(),
});

export const siteFrameSchema = z.strictObject({
  /** `boxed` — сайт по центру на фоне страницы (SB89). */
  mode: z.enum(["full", "boxed"]),
  /** Тень рамки, % (50 — прежняя тень); v1 true / false → 50 / 0. */
  shadow: num(TL.frameShadow),
  /**
   * Ширина рамки в режиме boxed, целые px 960–1920 (прежние 1280 / 1440 /
   * 1536 / 1600 — подсказки); старым документам без ключа — 1440.
   */
  maxWidth: z.number().int().min(TL.frameMaxWidth.min).max(TL.frameMaxWidth.max),
});

/** Состав бренда в шапке (SB90). На телефоне слоган скрывается сам. */
export const SITE_HEADER_BRAND_MODES = ["logo", "logo+name", "logo+name+tagline", "name"] as const;

export const siteHeaderSchema = z.strictObject({
  /** SB90: место логотипа — слева, по центру, справа, над меню. */
  logoPosition: z.enum(["left", "center", "right", "above"]),
  /**
   * `transparent` — только поверх первого блока с фото; на остальных
   * страницах витрина рисует светлую (uiux-design-r1 §2).
   */
  tone: z.enum(["light", "dark", "transparent"]),
  /**
   * Заливка шапки — роль темы или свой цвет (SB8, 2026-10-11). Тон выше —
   * производный от неё (readSiteTheme → syncSurfaceTones), кроме прозрачной
   * шапки: там заливка — вид после прокрутки и на страницах без фото.
   * Старым темам — из прежнего тона (deriveMissingSurfaceFills).
   */
  fill: siteSurfaceFillSchema,
  /**
   * Сколько пунктов меню слева от логотипа (разделитель «здесь логотип»,
   * SB73). Учитывается только при `logoPosition = center`; null — поровну.
   */
  splitAt: z.number().int().min(0).max(20).nullable(),
  brandMode: z.enum(SITE_HEADER_BRAND_MODES),
  menuCase: z.enum(["asis", "upper"]),
  /** Закреплять шапку при прокрутке. */
  sticky: z.boolean(),
  /** «Логотип крупнее шапки, выступает вниз» (SB73); только при center. */
  logoOversize: z.boolean(),
});

export const siteFooterShowSchema = z.strictObject({
  brand: z.boolean(),
  tagline: z.boolean(),
  contacts: z.boolean(),
  menu: z.boolean(),
  signature: z.boolean(),
  decor: z.boolean(),
  copyright: z.boolean(),
});

export const siteFooterSchema = z.strictObject({
  template: z.enum(["row", "columns"]),
  /** Производный от fill (readSiteTheme → syncSurfaceTones). */
  tone: z.enum(["light", "dark"]),
  /** Заливка подвала — роль темы или свой цвет (SB8, 2026-10-11); старым темам — из тона. */
  fill: siteSurfaceFillSchema,
  /**
   * Что показывать в подвале (uiux-design-r1 §2, вкладка «Подвал»). Пустое
   * значение не показывается и при `true` (SB21). Юридическую строку
   * выключить нельзя — её здесь нет (SB19).
   */
  show: siteFooterShowSchema,
  decorHideOnMobile: z.boolean(),
  /**
   * Нижняя полоса — вид юр. строки и порядок ссылок платформы (SB118,
   * footer-legal.ts). Старым темам — умолчание через mergeMissingKeys.
   */
  legal: siteFooterLegalSchema,
});

export const siteSpacingSchema = z.strictObject({
  /**
   * Плотность отступов (фаза §7.13; SB120 этап 2 — числом): множитель шкалы
   * ступеней `--site-space-*` 0,5–1,6 (css.ts). v1 compact / normal /
   * spacious → 0,8 / 1 / 1,25; документам без раздела — 1.
   */
  density: num(TL.density),
  /**
   * Доля отступов секций на телефоне от ПК (SB120 этап 1, db-design-sb120-stage1
   * §1; заменяет `--site-space-mobile` витрины). Секция может задать свою
   * (`look.space.phoneFactor`). Старым документам — 0,6 через
   * `mergeMissingKeys`, версия схемы не меняется (как `density`).
   */
  phoneFactor: z
    .number()
    .finite()
    .min(SECTION_LOOK_LIMITS.phoneFactor.min)
    .max(SECTION_LOOK_LIMITS.phoneFactor.max),
});

export const siteThemeSchema = z.strictObject({
  colors: siteColorsSchema,
  fonts: siteFontsSchema,
  type: siteTypeSchema,
  shapes: siteShapesSchema,
  backdrop: siteBackdropSchema,
  frame: siteFrameSchema,
  header: siteHeaderSchema,
  footer: siteFooterSchema,
  spacing: siteSpacingSchema,
  /**
   * Вид корзины (SB120 этап 4, cart.ts). Версия схемы та же (2): документам
   * без раздела — `DEFAULT_SITE_CART` через `mergeMissingKeys`, он равен
   * нынешней панели.
   */
  cart: siteCartSchema,
  /**
   * Свои строки блока «Ссылки» подвала (SB142, footer-docs.ts). Отдельный
   * раздел, а не поле `footer.legal`: при откате старый код отбросит только
   * его, подвал останется. Версия схемы та же: документам без раздела —
   * `{ items: [] }` через `mergeMissingKeys`.
   */
  footerDocs: siteFooterDocsSchema,
  /**
   * Вид баннера согласия на cookie (Р3г, SB134, consent-banner.ts). Версия
   * схемы та же (2): документам без раздела — `DEFAULT_SITE_CONSENT_BANNER`
   * через `mergeMissingKeys`, он равен нынешней полосе.
   */
  consentBanner: siteConsentBannerSchema,
});

export type SiteTheme = z.infer<typeof siteThemeSchema>;
export type SiteThemeSection = keyof SiteTheme;
export type SiteHeaderBrandMode = SiteTheme["header"]["brandMode"];
export type SiteBackdropImage = SiteTheme["backdrop"]["images"][number];
export type SitePageBgImage = NonNullable<SiteTheme["backdrop"]["image"]>;
/** Плотность отступов — множитель (SB120 этап 2); строки v1 — `SiteSpacingDensityV1` (legacy-v1.ts). */
export type SiteSpacingDensity = SiteTheme["spacing"]["density"];

/**
 * Все части документа — исчерпывающим списком на уровне типов (SB120 этап 4,
 * db-design-sb120-stage4 §1.3): `readSiteTheme` собирает итог только из
 * частей списка, и забытая строка молча теряла бы раздел при каждом чтении.
 * `satisfies Record<SiteThemeSection, 0>` — новая часть схемы без строки
 * здесь (или лишняя строка) не скомпилируется. Порядок — порядок схемы.
 */
const SITE_THEME_SECTION_SET = {
  colors: 0,
  fonts: 0,
  type: 0,
  shapes: 0,
  backdrop: 0,
  frame: 0,
  header: 0,
  footer: 0,
  spacing: 0,
  cart: 0,
  footerDocs: 0,
  consentBanner: 0,
} as const satisfies Record<SiteThemeSection, 0>;

/** Части документа в порядке схемы — для чтения по частям. */
export const SITE_THEME_SECTIONS: readonly SiteThemeSection[] = Object.freeze(
  Object.keys(SITE_THEME_SECTION_SET) as SiteThemeSection[],
);

/**
 * Нейтральное умолчание платформы (SB4): белый фон, тёмный текст, системный
 * шрифт, прямоугольные кнопки, без задника и стартовых картинок. Тем же
 * значением отвечает витрина, пока у магазина нет ни одной публикации.
 * Числа — прежнее умолчание v1, переведённое таблицами §1 (тест:
 * `DEFAULT_SITE_THEME` = подъём прежнего): заголовки ×1, разрядка 0,08 em,
 * кнопки 0 px, скругление 0/0, рамка карточек 1 px, тень 0, свечение 0,
 * рамка сайта во всю ширину (1440, тень 0), плотность ×1.
 */
export const DEFAULT_SITE_THEME: SiteTheme = deepFreeze({
  colors: {
    pageBg: "#ffffff",
    cardBg: "#ffffff",
    panelBg: "#f4f4f5",
    text: "#18181b",
    textMuted: "#52525b",
    button: "#18181b",
    accent: "#1d4ed8",
    darkBg: "#18181b",
    onDark: "#fafafa",
  },
  fonts: { heading: SITE_SYSTEM_FONT_KEY, body: SITE_SYSTEM_FONT_KEY, accent: null },
  type: { headingWeight: 700, headingCase: "asis", headingScale: 1, eyebrowFrom: "body", eyebrowTracking: 0.08 },
  shapes: {
    buttonRadius: 0,
    buttonArrow: false,
    buttonCase: "asis",
    radius: { card: 0, field: 0 },
    // Цвета рамки нет: «как у сайта» (линия) — наследуемое, в умолчание не входит (§1).
    cards: { border: { width: 1 }, shadow: 0 },
  },
  // Цвета свечения нет: акцент — наследуемое.
  backdrop: { mode: "off", haze: { strength: 0 }, images: [], image: null },
  frame: { mode: "full", shadow: 0, maxWidth: 1440 },
  header: {
    logoPosition: "left",
    tone: "light",
    fill: { kind: "role", role: "pageBg" },
    splitAt: null,
    brandMode: "logo+name",
    menuCase: "asis",
    sticky: true,
    logoOversize: false,
  },
  footer: {
    template: "row",
    tone: "light",
    fill: { kind: "role", role: "cardBg" },
    show: {
      brand: true,
      tagline: true,
      contacts: true,
      menu: true,
      signature: true,
      decor: true,
      copyright: true,
    },
    decorHideOnMobile: true,
    legal: defaultSiteFooterLegal(),
  },
  spacing: { density: TL.density.default, phoneFactor: SECTION_LOOK_LIMITS.phoneFactor.default },
  // Вид корзины — нынешняя панель (SB120 этап 4, cart.ts); копия замороженного умолчания.
  cart: structuredCloneJson(DEFAULT_SITE_CART),
  // Своих строк подвала нет (SB142, footer-docs.ts).
  footerDocs: structuredCloneJson(DEFAULT_SITE_FOOTER_DOCS),
  // Баннер cookie — нынешняя полоса (Р3г, consent-banner.ts).
  consentBanner: structuredCloneJson(DEFAULT_SITE_CONSENT_BANNER),
});

/** Частичное значение на любой глубине; массивы — целиком (рисунки задника). */
type DeepPartial<T> = T extends readonly unknown[]
  ? T
  : T extends object
    ? { [K in keyof T]?: DeepPartial<T[K]> }
    : T;

/** Частичный документ: любые вложенные ключи можно опустить. */
export type SiteThemePartial = DeepPartial<SiteTheme>;

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * Дополняет `value` ключами из `defaults` — только отсутствующими
 * (`undefined` или ключа нет). `null`, массивы и примитивы из `value` не
 * трогаются: `accent: null` — осознанный выбор «без акцента», а не дыра.
 * Лишние ключи `value` сохраняются — их отсеет схема.
 *
 * Варианты одного объединения (`kind` разный: заливка «роль» против «свой
 * цвет», фон «роль» против «фото») не сливаются: ключ одного варианта в
 * другом — лишний, строгая схема отвергла бы всю часть.
 */
export function mergeMissingKeys(defaults: unknown, value: unknown): unknown {
  if (value === undefined) return structuredCloneJson(defaults);
  if (!isPlainObject(defaults) || !isPlainObject(value)) return value;
  const kinds = [defaults["kind"], value["kind"]];
  if (kinds.every((k) => typeof k === "string") && kinds[0] !== kinds[1]) return value;
  const result: Record<string, unknown> = { ...value };
  for (const [key, def] of Object.entries(defaults)) {
    result[key] = mergeMissingKeys(def, value[key]);
  }
  return result;
}

/**
 * Недостающие ключи документа темы — из умолчаний, как при чтении: сначала
 * заливка шапки/подвала из прежнего тона (deriveMissingSurfaceFills — иначе
 * тёмная шапка получила бы светлую заливку умолчания), затем
 * `mergeMissingKeys`. Для строгих путей (импорт пакета вида сайта в
 * apps/api, PATCH части THEME): дополнить и проверить `validateSiteTheme` —
 * лишний или опечатанный ключ по-прежнему ошибка. Вход не меняется (работаем
 * на копии).
 *
 * Первым шагом — подъём ступеней v1 в числа (`liftThemeV1`) при любой версии
 * (db-design-sb120-stage2 §2): иначе `mergeMissingKeys` оставил бы строку
 * `"soft"` на месте объекта `{ card, field }`, и схема заменила бы всю часть
 * умолчанием. Подъём идемпотентен: документ v2 проходит без изменений.
 */
export function fillSiteThemeDefaults(doc: unknown, defaults: SiteTheme = DEFAULT_SITE_THEME): unknown {
  const copy = liftThemeV1(doc).doc;
  deriveMissingSurfaceFills(copy);
  return mergeMissingKeys(defaults, isPlainObject(copy) ? copy : {});
}

/**
 * Слияние типизированного частичного документа с умолчаниями: значения
 * `partial` в приоритете, умолчания — только для отсутствующих ключей.
 */
export function mergeTheme(defaults: SiteTheme, partial: SiteThemePartial): SiteTheme {
  // Типы partial совпадают с типами SiteTheme по ключам — результат валиден.
  return mergeMissingKeys(defaults, partial) as SiteTheme;
}

/** Подъём документа с версии N на N+1. Ключ — исходная версия. */
type SiteThemeUpgrade = (doc: unknown) => unknown;

/**
 * Реестр подъёмов (OCP: новая версия — новая запись). 1 → 2 — ступени в
 * числа (SB120 этап 2, `liftThemeV1`); та же функция стоит первой строкой
 * `fillSiteThemeDefaults`, поэтому документ v1 с меткой «2» тоже читается.
 */
const SITE_THEME_UPGRADES: Readonly<Record<number, SiteThemeUpgrade>> = {
  1: (doc) => liftThemeV1(doc).doc,
};

/**
 * Поднимает сохранённый документ до текущей `SITE_THEME_SCHEMA` (откат к
 * старой ревизии, восстановление из копии). SQL при смене версии не нужен.
 * Документ из будущей версии или с неизвестной версией — ошибка вызывающего:
 * сервер старее данных, молча читать нельзя.
 */
export function upgradeTheme(doc: unknown, fromSchema: number): unknown {
  if (!Number.isInteger(fromSchema) || fromSchema < 1 || fromSchema > SITE_THEME_SCHEMA) {
    throw new Error(`site_theme_schema_unsupported: ${fromSchema}`);
  }
  let current = doc;
  for (let version = fromSchema; version < SITE_THEME_SCHEMA; version += 1) {
    const step = SITE_THEME_UPGRADES[version];
    if (!step) throw new Error(`site_theme_upgrade_missing: ${version}`);
    current = step(current);
  }
  return current;
}

/** Замечание проверки документа — путь и текст для человека. */
export interface SiteThemeIssue {
  /** Путь в документе: `colors.text`, `backdrop.images.0.mediaId`. */
  path: string;
  message: string;
}

export type SiteThemeValidation =
  | { ok: true; theme: SiteTheme }
  | { ok: false; issues: SiteThemeIssue[] };

const issuePath = (path: readonly PropertyKey[]): string => path.map(String).join(".");

/**
 * Строгая проверка документа, который прислал кабинет (PATCH части THEME).
 * Сервер отвечает 400 `site_theme_invalid` с `details: issues`.
 */
export function validateSiteTheme(input: unknown): SiteThemeValidation {
  const parsed = siteThemeSchema.safeParse(input);
  if (parsed.success) return { ok: true, theme: parsed.data };
  return {
    ok: false,
    issues: parsed.error.issues.map((issue) => ({
      path: issuePath(issue.path),
      message: issue.message,
    })),
  };
}

/**
 * Шкала заголовков из документа темы (SB120 этап 3а, db-design-sb120-stage3
 * §5) — вход подъёма секций v3 → v4 вместе с плотностью из того же
 * документа: число в пределах 0,75–1,5 — как есть; строка v1 s / m / l —
 * `HEADING_SCALE_V1` (0,875 / 1 / 1,125); иначе (нет ключа, битое,
 * вне пределов) — 1. Так тему читает и `readSiteTheme`: тема v1 без ключа
 * получает 1 умолчанием, битая часть заменяется умолчанием.
 */
export function headingScaleOfTheme(theme: unknown): number {
  const type = isPlainObject(theme) ? theme["type"] : undefined;
  const scale = isPlainObject(type) ? type["headingScale"] : undefined;
  const { min, max, default: fallback } = TL.headingScale;
  if (typeof scale === "number") return Number.isFinite(scale) && scale >= min && scale <= max ? scale : fallback;
  if (typeof scale === "string" && Object.prototype.hasOwnProperty.call(HEADING_SCALE_V1, scale)) {
    return HEADING_SCALE_V1[scale as keyof typeof HEADING_SCALE_V1];
  }
  return fallback;
}

/**
 * Починка раздела до проверки схемой (OCP: раздел — запись). Нужна там, где
 * замена раздела целиком умолчанием теряла бы данные владельца из-за одной
 * битой строки: у своих строк подвала битая строка (документ с неверным id,
 * повтор) снимается по одной, остальные остаются.
 */
const SITE_THEME_SECTION_REPAIRS: Readonly<
  Partial<Record<SiteThemeSection, (value: unknown) => { value: unknown; issues: SiteThemeIssue[] }>>
> = {
  footerDocs: repairFooterDocs,
};

/** Итог чтения сохранённого документа. */
export interface SiteThemeReadResult {
  theme: SiteTheme;
  /** Что было заменено умолчанием (для журнала сервера; витрина не падает). */
  replaced: SiteThemeIssue[];
}

/**
 * Чтение сохранённого документа (черновик, ревизия, импорт):
 *   1) подъём версии (`upgradeTheme`);
 *   2) отсутствующие ключи — из `defaults` (`mergeMissingKeys`);
 *   3) неизвестный ключ шрифта — системный шрифт (db-design-r1 §1.1);
 *      «известный» = есть в `registry`: каталог платформы + свои шрифты
 *      магазина (SB99). Ключ `shop:` без записи в реестре (шрифт удалён или
 *      реестр не передан) тоже заменяется — CSS на него не ссылается;
 *   4) часть с починкой (`SITE_THEME_SECTION_REPAIRS`) сначала чинится по
 *      строкам — снятое попадает в `replaced`;
 *   5) часть, не прошедшая схему, целиком заменяется умолчанием — одна
 *      битая часть не должна обесцвечивать весь сайт.
 * Ошибка версии (данные новее кода) пробрасывается — см. `upgradeTheme`.
 */
export function readSiteTheme(
  raw: unknown,
  fromSchema: number = SITE_THEME_SCHEMA,
  defaults: SiteTheme = DEFAULT_SITE_THEME,
  registry: SiteFontRegistry = PLATFORM_FONT_REGISTRY,
): SiteThemeReadResult {
  const upgraded = upgradeTheme(raw, fromSchema);
  const merged = fillSiteThemeDefaults(upgraded, defaults);
  const doc = isPlainObject(merged) ? merged : {};
  const replaced: SiteThemeIssue[] = [];
  if (!isPlainObject(upgraded)) {
    replaced.push({ path: "", message: "Документ оформления не объект — взят вид по умолчанию" });
  }

  const fonts = doc["fonts"];
  if (isPlainObject(fonts)) {
    for (const role of ["heading", "body", "accent"] as const) {
      const key = fonts[role];
      if (typeof key === "string" && !registry.has(key)) {
        fonts[role] = SITE_SYSTEM_FONT_KEY;
        replaced.push({
          path: `fonts.${role}`,
          message: isShopFontKey(key)
            ? `Своего шрифта «${key}» нет у магазина — взят системный`
            : `Шрифта «${key}» нет в каталоге — взят системный`,
        });
      }
    }
  }

  const result: Record<string, unknown> = {};
  for (const section of SITE_THEME_SECTIONS) {
    const repair = SITE_THEME_SECTION_REPAIRS[section];
    const repaired = repair ? repair(doc[section]) : { value: doc[section], issues: [] };
    const parsed = siteThemeSchema.shape[section].safeParse(repaired.value);
    if (parsed.success) {
      result[section] = parsed.data;
      replaced.push(...repaired.issues);
    } else {
      result[section] = structuredCloneJson(defaults[section]);
      replaced.push({
        path: section,
        message: `Часть «${section}» не прошла проверку — взято умолчание`,
      });
    }
  }
  // Все части прошли свои схемы — документ целиком валиден. Тон шапки и
  // подвала — производный от заливки (прежние потребители читают тон).
  return { theme: syncSurfaceTones(result as SiteTheme), replaced };
}

/** Копия JSON-значения (умолчания заморожены, отдавать их наружу нельзя). */
function structuredCloneJson<T>(value: T): T {
  return value === undefined ? value : (JSON.parse(JSON.stringify(value)) as T);
}
